import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Check, ChevronRight, Building, Loader2, AlertCircle, X, Eye } from 'lucide-react';
import MatchingModal from '../components/MatchingModal';
import MatchReport from '../components/MatchReport';

const STEPS = ['Application Form', 'Match Report', 'Review & Submit'];

const PublicApply = () => {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchReport, setMatchReport] = useState(null);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState('');
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const [uploadedDocUrls, setUploadedDocUrls] = useState([]);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [showCvViewer, setShowCvViewer] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    locationCity: '',
    noticePeriod: '',
    salaryExpectation: '',
    coverLetter: '',
    linkedinProfile: ''
  });

  useEffect(() => {
    fetchPosition();
  }, [id]);

  const fetchPosition = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/positions/${id}`
      );
      if (response.data.success) {
        setPosition(response.data.data);
        setOrg(response.data.data.organizations);
      }
    } catch (err) {
      console.error('Error fetching position:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setResumeFile(file);
      setError('');
    }
  };

  const isFormValid = () => {
    return formData.firstName && formData.lastName && formData.email &&
      formData.phone && formData.locationCity && formData.noticePeriod &&
      formData.salaryExpectation && resumeFile;
  };

  // Step 1 → Step 2: Upload resume + generate match report
  const handleCreateApplication = async () => {
    if (!isFormValid()) {
      setError('Please fill in all required fields and upload your CV');
      return;
    }

    setAnalyzing(true);
    setError('');

    try {
      // First upload the resume
      const uploadForm = new FormData();
      uploadForm.append('resume', resumeFile);

      const uploadResp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/upload-resume`,
        uploadForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (!uploadResp.data.success) throw new Error('Resume upload failed');
      setResumeUrl(uploadResp.data.data.resumeUrl);

      // Upload additional documents
      const uploadedDocUrls = [];
      for (const doc of additionalDocs) {
        const docForm = new FormData();
        docForm.append('resume', doc);
        const docResp = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/upload-resume`,
          docForm,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (docResp.data.success) {
          uploadedDocUrls.push({
            name: doc.name,
            url: docResp.data.data.resumeUrl,
            size: doc.size,
          });
        }
      }
      setUploadedDocUrls(uploadedDocUrls);

      // Generate match report using AI
      const matchForm = new FormData();
      matchForm.append('resume', resumeFile);
      matchForm.append('position_data', JSON.stringify({
        title: position.title,
        must_have_skills: position.must_have_skills || [],
        nice_to_have_skills: position.nice_to_have_skills || [],
        description: position.description || '',
        responsibilities: position.responsibilities || '',
        profile_requirements: position.profile_requirements || ''
      }));
      additionalDocs.forEach(doc => {
        matchForm.append('additional_docs', doc);
      });

      const matchResp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/match-score`,
        matchForm,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );

      if (matchResp.data.success) {
        setMatchReport(matchResp.data.data);
        setStep(1);
      } else {
        throw new Error(matchResp.data.message || 'Match scoring failed');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.message || err.message || 'Something went wrong. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Step 3: Final submit
  const handleSubmitApplication = async () => {
    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/apply`,
        {
          position_id: id,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          location_city: formData.locationCity,
          notice_period: formData.noticePeriod,
          salary_expectation: parseInt(formData.salaryExpectation),
          resume_url: resumeUrl,
          cover_letter: formData.coverLetter,
          linkedin_profile: formData.linkedinProfile,
          match_report: matchReport,
          match_score: matchReport?.score || null,
          additional_documents: uploadedDocUrls
        }
      );

      if (response.data.success) {
        setStep(3); // Success state
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-gray-500">Loading...</p></div>;
  }

  if (!position) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-gray-500">Position not found</p></div>;
  }

  // Success State (Step 3 complete)
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="application-success">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-gray-900">{org?.name}</span>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Application Submitted!</h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Thank you for applying to <strong>{position.title}</strong> at <strong>{org?.name}</strong>.
            We'll be in touch soon.
          </p>
          <Link
            to={`/${slug}/jobs`}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            View more positions at {org?.name}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-apply">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <Building className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-gray-900">{org?.name}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Back */}
        <Link
          to={`/${slug}/jobs/${id}`}
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to job
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Apply for {position.title}</h1>
        <p className="text-gray-500 text-sm mb-8">{org?.name} &middot; {position.location}</p>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-2 ${i <= step ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                  i < step ? 'bg-blue-600 text-white border-blue-600' :
                  i === step ? 'border-blue-600 text-blue-600' :
                  'border-gray-300 text-gray-400'
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-2" data-testid="apply-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Step 0: Application Form */}
        {step === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5" data-testid="apply-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="apply-first-name" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="apply-last-name" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="apply-email" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="+49 123 456 789" data-testid="apply-phone" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location (City) *</label>
                <input type="text" name="locationCity" value={formData.locationCity} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g., Berlin" data-testid="apply-location" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period *</label>
                <select name="noticePeriod" value={formData.noticePeriod} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="apply-notice-period" required>
                  <option value="">Select...</option>
                  <option value="Immediate">Immediate</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="1 month">1 month</option>
                  <option value="2 months">2 months</option>
                  <option value="3 months">3 months</option>
                  <option value="6 months">6 months</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Expectation (Annual, EUR) *</label>
                <input type="number" name="salaryExpectation" value={formData.salaryExpectation} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g., 65000" data-testid="apply-salary" required />
              </div>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resume / CV *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                {resumeFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{resumeFile.name}</p>
                      <p className="text-xs text-gray-500">{(resumeFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={() => setResumeFile(null)} className="text-red-500 hover:text-red-700 text-sm font-medium ml-2">Remove</button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600"><span className="text-blue-600 font-medium">Click to upload</span> or drag and drop</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX (max 10MB)</p>
                    <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleFileChange} className="hidden" data-testid="apply-resume-input" />
                  </label>
                )}
              </div>
            </div>

            {/* Additional Documents */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Documents <span className="text-gray-400">(optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">Certificates, portfolios, references, or other supporting documents. These will be included in your AI match evaluation.</p>
              <div className="space-y-2">
                {additionalDocs.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                    <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
                      <p className="text-xs text-gray-400">{(doc.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => setAdditionalDocs(prev => prev.filter((_, i) => i !== idx))}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`remove-doc-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(
                  <label className="flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-lg py-3 px-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">Add document</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg"
                      className="hidden"
                      data-testid="apply-additional-doc-input"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 10 * 1024 * 1024) {
                            setError('Each file must be less than 10MB');
                            return;
                          }
                          setAdditionalDocs(prev => [...prev, file]);
                          setError('');
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Optional Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter <span className="text-gray-400">(optional)</span></label>
              <textarea name="coverLetter" value={formData.coverLetter} onChange={handleInputChange} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Tell us why you're interested in this role..." data-testid="apply-cover-letter" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile <span className="text-gray-400">(optional)</span></label>
              <input type="url" name="linkedinProfile" value={formData.linkedinProfile} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="https://linkedin.com/in/..." data-testid="apply-linkedin" />
            </div>

            <div className="pt-4">
              <button
                onClick={handleCreateApplication}
                disabled={!isFormValid() || analyzing}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="create-application-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analyzing your profile...
                  </>
                ) : (
                  'Create Application'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Match Report */}
        {step === 1 && matchReport && (
          <div className="space-y-6" data-testid="match-report">
            <MatchReport
              data={matchReport}
              candidateName={`${formData.firstName} ${formData.lastName}`}
              candidateInfo={{
                noticePeriod: formData.noticePeriod,
                salaryExpectation: formData.salaryExpectation,
                locationCity: formData.locationCity
              }}
            />

            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep(0)} className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                Back
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                data-testid="continue-to-review-btn"
              >
                Continue to Review
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review & Submit */}
        {step === 2 && (
          <div className="space-y-6" data-testid="review-step">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Review Your Application</h2>

              {/* Position */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Position</h3>
                <p className="text-lg font-medium text-gray-900">{position.title}</p>
                <p className="text-sm text-gray-600">{org?.name} &middot; {position.location}</p>
              </div>

              {/* Personal Info */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-900">{formData.firstName} {formData.lastName}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium text-gray-900">{formData.email}</span></div>
                  <div><span className="text-gray-500">Phone:</span> <span className="font-medium text-gray-900">{formData.phone}</span></div>
                  <div><span className="text-gray-500">Location:</span> <span className="font-medium text-gray-900">{formData.locationCity}</span></div>
                  <div><span className="text-gray-500">Notice Period:</span> <span className="font-medium text-gray-900">{formData.noticePeriod}</span></div>
                  <div><span className="text-gray-500">Salary Expectation:</span> <span className="font-medium text-gray-900">EUR {parseInt(formData.salaryExpectation).toLocaleString()}</span></div>
                </div>
              </div>

              {/* Documents */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Documents</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{resumeFile?.name || 'CV'}</p>
                        <p className="text-xs text-gray-500">Resume / CV</p>
                      </div>
                    </div>
                    {resumeUrl && (
                      <button onClick={() => setShowCvViewer(true)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        data-testid="public-view-cv-btn">
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    )}
                  </div>
                  {matchReport && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">AI Match Report</p>
                          <p className="text-xs text-gray-500">{matchReport.score}% — {matchReport.verdict}</p>
                        </div>
                      </div>
                      <button onClick={() => setShowReportViewer(true)}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                        data-testid="public-view-report-btn">
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </div>
                  )}
                </div>
                {formData.linkedinProfile && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-500">LinkedIn:</span>{' '}
                    <a href={formData.linkedinProfile} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{formData.linkedinProfile}</a>
                  </div>
                )}
              </div>

              {/* Cover Letter */}
              {formData.coverLetter && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Cover Letter</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{formData.coverLetter}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                Back
              </button>
              <button
                onClick={handleSubmitApplication}
                disabled={submitting}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:bg-gray-400 flex items-center justify-center gap-2"
                data-testid="submit-application-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </div>
        )}

        {/* CV Viewer Modal */}
        {showCvViewer && resumeUrl && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <h3 className="font-semibold text-gray-900">CV / Resume</h3>
                <button onClick={() => setShowCvViewer(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <iframe src={resumeUrl} className="flex-1 w-full" title="CV Viewer" />
            </div>
          </div>
        )}

        {/* Report Viewer Modal */}
        {showReportViewer && matchReport && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <h3 className="font-semibold text-gray-900">AI Match Report</h3>
                <button onClick={() => setShowReportViewer(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto bg-white">
                <MatchReport
                  data={matchReport}
                  candidateName={`${formData.firstName} ${formData.lastName}`}
                  candidateInfo={{
                    noticePeriod: formData.noticePeriod,
                    salaryExpectation: formData.salaryExpectation,
                    locationCity: formData.locationCity
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <MatchingModal active={analyzing} />
    </div>

  );
};

export default PublicApply;